import React from 'react';
import { Modal, Table, message, Card, Tag, Space, Typography, Button } from 'antd';
import { ActivityGameMatchInfo, MatchRoundResult } from '../../interface';
import { getMatchActivityRoundResult } from '../../apiBridge';
import { isMatchWinner, matchParticipantDisplayName } from './activityMatch';
import dayjs from 'dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface MatchResultModalProps {
    visible: boolean;
    onCancel: () => void;
    currentMatch: ActivityGameMatchInfo | null;
    activityId: string;
}

const renderStats = (stats: Record<string, unknown>) => {
    if (!stats || typeof stats !== 'object') return null;
    
    const blueStats: Record<string, unknown> = {};
    const redStats: Record<string, unknown> = {};
    const otherStats: Record<string, unknown> = {};

    Object.entries(stats).forEach(([key, value]) => {
        if (key.startsWith('Blue')) {
            blueStats[key.replace('Blue', '')] = value;
        } else if (key.startsWith('Red')) {
            redStats[key.replace('Red', '')] = value;
        } else {
            otherStats[key] = value;
        }
    });

    return (
        <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: 16 }}>
                <Title level={5} style={{ marginBottom: 8 }}>Blue Side</Title>
                {Object.entries(blueStats).map(([key, value]) => (
                    <div key={`blue-${key}`}>
                        {key}: {String(value)}
                    </div>
                ))}
            </div>
            <div style={{ marginBottom: 16 }}>
                <Title level={5} style={{ marginBottom: 8 }}>Red Side</Title>
                {Object.entries(redStats).map(([key, value]) => (
                    <div key={`red-${key}`}>
                        {key}: {String(value)}
                    </div>
                ))}
            </div>
            <div>
                <Title level={5} style={{ marginBottom: 8 }}>Other Info</Title>
                {Object.entries(otherStats).map(([key, value]) => (
                    <div key={`other-${key}`}>
                        {key}: {String(value)}
                    </div>
                ))}
            </div>
        </div>
    );
};

const MatchResultModal: React.FC<MatchResultModalProps> = ({
    visible,
    onCancel,
    currentMatch,
    activityId
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = React.useState(false);
    const [roundResults, setRoundResults] = React.useState<MatchRoundResult[]>([]);
    const [statsModalVisible, setStatsModalVisible] = React.useState(false);
    const [currentStats, setCurrentStats] = React.useState<Record<string, unknown> | null>(null);

    const formatTime = (startTime: string, endTime: string) => {
        const start = dayjs(startTime);
        const end = dayjs(endTime);
        
        if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
            return `${start.format('YYYY-MM-DD')} ${start.format('HH:mm:ss')} - ${end.format('HH:mm:ss')}`;
        }
        return `${start.format('YYYY-MM-DD HH:mm:ss')} - ${end.format('HH:mm:ss')}`;
    };

    const formatDuration = (duration: number) => {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        const secondsStr = String(seconds).padStart(2, '0');
        return `${minutes}:${secondsStr}`;
    };

    const getStateDisplay = (state: string) => {
        switch (state) {
            default:
                return state;
        }
    };

    const getStateColor = (state: string) => {
        switch (state) {
            default:
                return 'default';
        }
    };

    React.useEffect(() => {
        let cancelled = false;

        const loadMatchResults = async () => {
            if (!currentMatch) return;
            
            setLoading(true);
            setRoundResults([]);
            try {
                const res = await getMatchActivityRoundResult(activityId, currentMatch.id, true);
                const result = await res.json() as {
                    match?: ActivityGameMatchInfo;
                    items?: MatchRoundResult[];
                    error?: string;
                };
                if (!res.ok) {
                    throw new Error(result.error || "Failed to load match results");
                }
                if (!Array.isArray(result.items)) {
                    throw new Error("Invalid match result response");
                }
                if (!cancelled) {
                    setRoundResults(result.items);
                }
            } catch (error) {
                if (!cancelled) {
                    messageApi.error(error instanceof Error ? error.message : "Failed to load match results");
                    setRoundResults([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        if (visible && currentMatch) {
            loadMatchResults();
        }

        return () => {
            cancelled = true;
        };
    }, [visible, currentMatch, activityId, messageApi]);

    const groupedResults = React.useMemo(() => {
        const groups = new Map<number, MatchRoundResult[]>();
        roundResults.forEach(result => {
            const group = groups.get(result.game_round) || [];
            group.push(result);
            groups.set(result.game_round, group);
        });
        return Array.from(groups.entries()).sort(([a], [b]) => a - b);
    }, [roundResults]);

    const getCurrentResult = (results: MatchRoundResult[]) => {
        return results.find(r => r.is_current) || results[results.length - 1];
    };

    return (
        <>
            {contextHolder}
            <Modal
                title="Match Result"
                open={visible}
                onCancel={onCancel}
                footer={null}
                width={1000}
            >
                {currentMatch && (
                    <div>
                        <Card 
                            loading={loading}
                            style={{ marginBottom: 24 }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Title level={4} style={{ margin: 0 }}>Match Info</Title>
                                <Space wrap>
                                    <Tag color="blue">Bout: {currentMatch.bout_number}</Tag>
                                    <Tag color="blue">Court: {currentMatch.court}</Tag>
                                </Space>
                                <Space wrap>
                                    <Tag color={isMatchWinner(currentMatch, currentMatch.participants[0]?.id) ? 'success' : 'default'}>
                                        Player 1: {matchParticipantDisplayName(currentMatch.participants[0], currentMatch.state)}
                                    </Tag>
                                    <Tag color={isMatchWinner(currentMatch, currentMatch.participants[1]?.id) ? 'success' : 'default'}>
                                        Player 2: {matchParticipantDisplayName(currentMatch.participants[1], currentMatch.state)}
                                    </Tag>
                                </Space>
                            </Space>
                        </Card>
                        
                        {groupedResults.length > 0 ? (
                            groupedResults.map(([gameRound, results]) => {
                                const currentResult = getCurrentResult(results);
                                return (
                                    <Card 
                                        key={gameRound}
                                        style={{ marginBottom: 16 }}
                                        title={`Round ${gameRound}`}
                                        extra={
                                            <Space>
                                                {results.length > 1 && (
                                                    <Tag color="warning">
                                                        Reset Count: {results.length - 1}
                                                    </Tag>
                                                )}
                                                <Tag color={getStateColor(currentResult.state)}>
                                                    {getStateDisplay(currentResult.state)}
                                                </Tag>
                                            </Space>
                                        }
                                    >
                                        <Table
                                            dataSource={results}
                                            rowKey="id"
                                            pagination={false}
                                            rowClassName={(record) => record.is_current ? 'current-result-row' : ''}
                                            columns={[
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'state',
                                                    key: 'state',
                                                    width: 100,
                                                    render: (state) => (
                                                        <Tag color={getStateColor(state)}>
                                                            {getStateDisplay(state)}
                                                        </Tag>
                                                    )
                                                },
                                                {
                                                    title: 'Score',
                                                    key: 'score',
                                                    width: 200,
                                                    render: (_, record) => {
                                                        return (
                                                            <Space direction="vertical" size={0}>
                                                                <Text
                                                                    style={{ 
                                                                        color: isMatchWinner(record, currentMatch.participants[0]?.id) ? '#52c41a' : 'inherit',
                                                                    }}
                                                                >
                                                                     {matchParticipantDisplayName(currentMatch.participants[0], currentMatch.state, 'Unknown')}: 
                                                                     {isMatchWinner(record, currentMatch.participants[0]?.id) ? record.win_score : record.lose_score}
                                                                </Text>
                                                                <Text 
                                                                    style={{ 
                                                                        color: isMatchWinner(record, currentMatch.participants[1]?.id) ? '#52c41a' : 'inherit',
                                                                    }}
                                                                >
                                                                    {matchParticipantDisplayName(currentMatch.participants[1], currentMatch.state, 'Unknown')}: 
                                                                    {isMatchWinner(record, currentMatch.participants[1]?.id) ? record.win_score : record.lose_score}
                                                                </Text>
                                                            </Space>
                                                        );
                                                    }
                                                },
                                                {
                                                    title: 'Duration',
                                                    dataIndex: 'duration',
                                                    key: 'duration',
                                                    width: 120,
                                                    render: (duration, record) => (
                                                        <Text style={{ fontWeight: record.is_current ? 'bold' : 'normal' }}>
                                                            {formatDuration(duration)}
                                                        </Text>
                                                    )
                                                },
                                                {
                                                    title: 'Reset',
                                                    dataIndex: 'reset_count',
                                                    key: 'reset_count',
                                                    width: 100,
                                                    render: (resetCount, record) => (
                                                        <Text style={{ fontWeight: record.is_current ? 'bold' : 'normal' }}>
                                                            {resetCount}
                                                        </Text>
                                                    )
                                                },
                                                {
                                                    title: 'Time',
                                                    key: 'time',
                                                    width: 200,
                                                    render: (_, record) => (
                                                        <Text type="secondary" style={{ fontWeight: record.is_current ? 'bold' : 'normal' }}>
                                                            {formatTime(record.start_at, record.end_at)}
                                                        </Text>
                                                    )
                                                },
                                                {
                                                    title: 'Action',
                                                    key: 'action',
                                                    width: 100,
                                                    render: (_, record) => (
                                                        record.stats && Object.keys(record.stats).length > 0 ? (
                                                            <Button 
                                                                type="link" 
                                                                icon={<InfoCircleOutlined />}
                                                                onClick={() => {
                                                                    setCurrentStats(record.stats);
                                                                    setStatsModalVisible(true);
                                                                }}
                                                            >
                                                                Details
                                                            </Button>
                                                        ) : null
                                                    )
                                                }
                                            ]}
                                        />
                                    </Card>
                                );
                            })
                        ) : (
                            <Card>
                                {currentMatch?.state === 'finish' ? (
                                    <Space direction="vertical">
                                        <Typography.Text type="secondary">
                                            No match results available. This match may have been decided by GM.
                                        </Typography.Text>
                                        {currentMatch.reason && (
                                            <Typography.Text type="secondary">
                                                Reason: {currentMatch.reason}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                ) : currentMatch?.state === 'walkover' ? (
                                    <Typography.Text type="secondary">
                                        This is a walkover match. No results are available.
                                    </Typography.Text>
                                ) : (
                                    <Typography.Text type="secondary">
                                        No match results available at the moment.
                                    </Typography.Text>
                                )}
                            </Card>
                        )}
                    </div>
                )}
            </Modal>
            <Modal
                title="Match Statistics"
                open={statsModalVisible}
                onCancel={() => setStatsModalVisible(false)}
                footer={null}
                width={600}
            >
                {currentStats && renderStats(currentStats)}
            </Modal>
            <style>
                {`
                    .current-result-row {
                        background-color: #f6ffed;
                    }
                    .ant-table-tbody > tr.current-result-row:hover > td {
                        background-color: #f6ffed;
                    }
                `}
            </style>
        </>
    );
};

export default MatchResultModal; 
